import { ASSIST, START_LIVES } from '../data/tuning';
import { resetBossFight } from './boss/boss-fight';
import { resetAttacks } from './boss/attacks';
import { resetDecoys } from './boss/decoys';
import { entryAltitude, initialEnemies, placePlane, type LevelRun } from './level-runner';
import type { CoreEventInput } from './tick';

/**
 * Game over (GDD §6.2, rule 6), applied when DERRIBO ends on the last life (DERRIBO > OPERACION_FALLIDA):
 * the checkpoints of
 * the level are cleared, lives go back to 3 and the score to its value at the start of the level. The
 * level, the unlocks, the records and the dollar counter are kept; deaths were already counted (#37).
 * Idempotent. Returns the RUN_RESTORED event that the save (#47) writes.
 */
export function applyGameOver(run: LevelRun): CoreEventInput {
  const { core, state } = run;
  for (const checkpoint of run.checkpoints.checkpoints) {
    checkpoint.active = false;
    checkpoint.flare = 'red';
  }
  run.checkpoints.activeIndex = -1;
  state.checkpointId = null;
  state.outcome = 'OPERACION_FALLIDA';
  // Lives go back to the starting ones of the level: 3, or 5 when it was loaded with the assist mode (GDD §13.6, #138).
  core.lives = state.assisted ? ASSIST.startLives : START_LIVES;
  core.score = state.scoreAtLevelStart;
  core.pendingScore = 0;
  return {
    type: 'RUN_RESTORED',
    levelId: state.levelId,
    checkpointId: null,
    lives: core.lives,
    score: core.score,
    scoreAtLevelStart: state.scoreAtLevelStart,
    dollars: core.dollars,
  };
}

/**
 * "Reintentar" (#61): the same level from x 0 with every entity back to its definition, the restored
 * lives and score, and no checkpoint. Called by the OPERACION_FALLIDA > INTRO transition.
 */
export function retry(run: LevelRun): void {
  const { core, level, state } = run;
  if (run.boss) resetBossFight(run.boss);
  if (run.decoys) resetDecoys(run.decoys);
  if (run.attacks) resetAttacks(run.attacks);
  run.collectedPickups.clear();
  run.passedColumns.clear();
  initialEnemies(level).forEach((enemy, index) => Object.assign(run.enemies[index]!, enemy));
  state.outcome = 'EN_CURSO';
  state.checkpointId = null;
  // A retry after game over is a fresh attempt: it can earn the clean-run bonus (GDD §12).
  state.cleanRun = true;
  run.deathsSeen = core.deaths;
  core.score = state.scoreAtLevelStart;
  core.player = { x: 0, y: entryAltitude(level, 0), vy: 0 };
  placePlane(run, 0);
}
